import React, { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Reports() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [report, setReport] = useState(null);
  const [custReport, setCustReport] = useState(null);
  const [venueReport, setVenueReport] = useState([]);

  useEffect(() => {
    api.get("/admin/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/admin/reports/customers").then((r) => setCustReport(r.data)).catch(() => {});
    api.get("/admin/reports/venues").then((r) => setVenueReport(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (eventId) api.get(`/admin/reports/event/${eventId}`).then((r) => setReport(r.data)).catch(() => {});
    else setReport(null);
  }, [eventId]);

  const Row = ({ label, value, gold }) => (
    <div className="flex justify-between py-2 border-b border-[#27272A] text-sm">
      <span className="text-zinc-400">{label}</span><span className={gold ? "text-[#D4AF37] font-semibold" : "text-white"}>{value}</span>
    </div>
  );

  return (
    <div>
      <h1 className="font-display text-3xl uppercase text-white mb-6">Reports</h1>

      {/* Event report */}
      <div className="rounded-xl border border-[#27272A] bg-[#18181B] p-4 mb-6">
        <h2 className="font-oswald text-sm tracking-widest text-zinc-300 uppercase mb-3">Event Report</h2>
        <Select value={eventId} onValueChange={setEventId}>
          <SelectTrigger data-testid="report-event-select" className="w-full rounded-md bg-[#141417] border border-[#27272A] px-3 py-2.5 text-white text-sm"><SelectValue placeholder="Choose an event" /></SelectTrigger>
          <SelectContent>{events.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
        </Select>
        {report && (
          <div className="mt-4">
            <Row label="Tickets Available" value={report.tickets_available || "∞"} />
            <Row label="Tickets Sold" value={report.tickets_sold} />
            <Row label="Tickets Remaining" value={report.tickets_remaining ?? "∞"} />
            <Row label="Gross Revenue" value={money(report.gross_revenue)} gold />
            <Row label="Refunds" value={money(report.refunds)} />
            <Row label="Fees Collected" value={money(report.fees_collected)} />
            <Row label="Net (after refunds & fees)" value={money(report.net_after_fees)} gold />
            <Row label="Checked In" value={`${report.checked_in} / ${report.checked_in + report.not_checked_in}`} />
            <div className="mt-4">
              <p className="text-xs font-oswald tracking-widest text-zinc-500 uppercase mb-2">By Ticket Type</p>
              {report.breakdown.map((b, i) => (
                <div key={i} className="flex justify-between text-sm py-1.5 border-b border-[#27272A]">
                  <span className="text-zinc-300">{b.name} <span className="text-zinc-500">({money(b.price)})</span></span>
                  <span className="text-white">{b.sold} sold · {money(b.revenue)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Customer report */}
      {custReport && (
        <div className="rounded-xl border border-[#27272A] bg-[#18181B] p-4 mb-6">
          <h2 className="font-oswald text-sm tracking-widest text-zinc-300 uppercase mb-3">Customer Report</h2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[["Total", custReport.total_customers], ["New", custReport.new_customers], ["Returning", custReport.returning_customers], ["Email Opt-in", custReport.email_optins], ["SMS Opt-in", custReport.sms_optins]].map(([l, v]) => (
              <div key={l} className="rounded-lg bg-[#141417] p-3 text-center"><p className="text-2xl font-bold text-white">{v}</p><p className="text-[10px] text-zinc-500 uppercase mt-1">{l}</p></div>
            ))}
          </div>
        </div>
      )}

      {/* Venue report */}
      <div className="rounded-xl border border-[#27272A] bg-[#18181B] p-4">
        <h2 className="font-oswald text-sm tracking-widest text-zinc-300 uppercase mb-3">Venue Report</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[420px]">
            <thead className="text-zinc-400"><tr><th className="text-left py-2 font-medium">Venue</th><th className="text-center py-2 font-medium">Events</th><th className="text-center py-2 font-medium">Tickets</th><th className="text-right py-2 font-medium">Revenue</th></tr></thead>
            <tbody>
              {venueReport.map((v, i) => (
                <tr key={i} className="border-t border-[#27272A] text-zinc-200">
                  <td className="py-2">{v.venue} {v.abe_owned && <span className="text-[#D4AF37]">★</span>}</td>
                  <td className="py-2 text-center">{v.events_held}</td>
                  <td className="py-2 text-center">{v.tickets_sold}</td>
                  <td className="py-2 text-right text-[#D4AF37]">{money(v.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
